import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import ExpandMoreOutlined from "@mui/icons-material/ExpandMoreOutlined";
import EditOutlined from "@mui/icons-material/EditOutlined";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import CheckOutlined from "@mui/icons-material/CheckOutlined";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import CheckCircleOutlined from "@mui/icons-material/CheckCircleOutlined";
import AutoAwesomeOutlined from "@mui/icons-material/AutoAwesomeOutlined";
import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import TuneOutlined from "@mui/icons-material/TuneOutlined";
import Spinner from "common/components/base/spinner/Spinner";
import useOnClickOutside from "common/hooks/useOnClickOutside";
import {
  ONTOLOGY_WORKBENCH_STEPS,
  DUMMY_GENERATED_CQ_KPIS,
  CQ_ALERTS,
  CompetencyQuestionRow,
  GeneratedCqKpi,
  CqStatus
} from "../../data/create-ontology-questions.data";
import { OntologyService } from "../../data/ontology.service";
import SimplePagination from "../../components/SimplePagination";
import CqGenerationShimmer from "../../components/cq-generation-shimmer/CqGenerationShimmer";
import "./CreateOntologyQuestions.scss";

const BUSINESS_QUESTIONS_PAGE_SIZE = 5;
const CQ_TABLE_PAGE_SIZE = 200;

const ALERT_ICONS: Record<string, React.ReactNode> = {
  warning: <WarningAmberOutlined fontSize="small" />,
  info: <InfoOutlined fontSize="small" />,
  danger: <ErrorOutline fontSize="small" />
};

const STATUS_CLASS: Record<CqStatus, string> = {
  Approved: "create-questions-status-pill--approved",
  "Needs Review": "create-questions-status-pill--needs-review",
  Draft: "create-questions-status-pill--draft",
  Rejected: "create-questions-status-pill--rejected"
};

// Maps a CQ's status to the matching summary KPI card id (for local, single-row bumps).
const CQ_STATUS_KPI_ID: Record<CqStatus, string> = {
  Approved: "approved",
  "Needs Review": "needs-review",
  Draft: "draft",
  Rejected: "rejected"
};

// Backend sends free-text source labels (casing/spacing varies); normalise to one of three
// categories so pill styling and the Source filter treat "AI+Edited", "ai edited", etc. the same.
type SourceCategory = "ai" | "ai-edited" | "manual";

const getSourceCategory = (source: string): SourceCategory => {
  const normalized = source.trim().toUpperCase();
  if (normalized.includes("MANUAL")) return "manual";
  if (normalized.includes("EDIT")) return "ai-edited";
  return "ai";
};

const SOURCE_PILL_CLASS: Record<SourceCategory, string> = {
  ai: "create-questions-source-pill--ai",
  "ai-edited": "create-questions-source-pill--ai-edited",
  manual: "create-questions-source-pill--manual"
};

const getSourceClass = (source: string): string =>
  SOURCE_PILL_CLASS[getSourceCategory(source)];

const SOURCE_FILTER_OPTIONS: { value: SourceCategory; label: string }[] = [
  { value: "ai", label: "AI" },
  { value: "ai-edited", label: "AI + EDITED" },
  { value: "manual", label: "MANUAL" }
];

// Value sent to the backend's ?source= query param for each filter category.
const SOURCE_CATEGORY_QUERY_VALUE: Record<SourceCategory, string> = {
  ai: "AI",
  "ai-edited": "AI + EDITED",
  manual: "MANUAL"
};

const STATUS_FILTER_OPTIONS: CqStatus[] = [
  "Draft",
  "Needs Review",
  "Approved",
  "Rejected"
];

const SUMMARY_CARD_CLASS: Record<string, string> = {
  approved: "create-questions-summary-card--approved",
  "needs-review": "create-questions-summary-card--needs-review"
};

// Renders ISO timestamps as "12 Mar 2026, 14:32 UTC" instead of the raw ISO string.
const formatDateTime = (value: string | null | undefined): string => {
  if (!value) return "Not recorded yet";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not recorded yet";
  const datePart = date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC"
  });
  const timePart = date.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "UTC"
  });
  return `${datePart}, ${timePart} UTC`;
};

const CQ_GENERATION_POLL_INTERVAL_MS = 6000;
const CQ_GENERATION_POLL_TIMEOUT_MS = 240000;
// Backend stage name while the queue worker is actively generating competency questions.
const CQ_GENERATION_IN_PROGRESS_STAGE = "CQ generation in progress";
// Backend stage name once business questions have been captured for the initiative.
const BQ_GENERATED_STAGE = "BQ_GENERATED";
// Backend stage name once competency questions have been generated for the initiative.
const CQ_GENERATED_STAGE = "CQ_GENERATED";
// Backend stage name when the queue worker failed to generate competency questions.
const CQ_GENERATION_FAILED_STAGE = "CQ Generation Failed";
// Backend stage name when entity/relationship generation failed after CQs were approved;
// the CQ table is still shown here so the user can review/retry from this screen.
const ENTITY_GENERATION_FAILED_STAGE = "Entity Generation Failed";

// Polls the initiative while it's stuck on the in-progress stage, returning the stage it
// eventually lands on (or null if the timeout elapses first).
const waitForCqGeneration = async (
  initiativeId: string
): Promise<string | null> => {
  const deadline = Date.now() + CQ_GENERATION_POLL_TIMEOUT_MS;
  while (Date.now() < deadline) {
    const initiative = await OntologyService.getInitiativeById(initiativeId);
    if (initiative.currentStageName !== CQ_GENERATION_IN_PROGRESS_STAGE) {
      return initiative.currentStageName;
    }
    await new Promise((resolve) =>
      setTimeout(resolve, CQ_GENERATION_POLL_INTERVAL_MS)
    );
  }
  return null;
};

// Tracks the backend business-question id alongside its text so edits can PUT to the right record.
interface BusinessQuestionEntry {
  id: string | null;
  question: string;
  bqCode: string | null;
}

// Local, editable snapshot of a CQ row while the edit drawer is open; fields mirror the backend's
// OntologyCompetencyQuestionItemResponse payload exactly.
// Per manager requirement, only Competency Question, Business Question, and Notes are editable.
interface CqEditForm {
  cqId: string;
  initiativeId: string;
  businessQuestionId: string;
  businessQuestionCode: string | null;
  businessQuestionText: string | null;
  cqCode: string;
  questionText: string;
  confidenceScore: number | null;
  status: string;
  explanation: string | null;
  evidenceSource: string | null;
  metadata: string | null;
  createdAt: string;
  updatedAt: string | null;
  reviewedAt: string | null;
}

const CreateOntologyQuestions: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const initiativeId = (location.state as { initiativeId?: string } | null)
    ?.initiativeId;

  // Guards against landing here directly (e.g. bookmarked/refreshed URL) without an initiative.
  useEffect(() => {
    if (!initiativeId) {
      navigate("/home", { replace: true });
    }
  }, [initiativeId, navigate]);

  const [businessQuestions, setBusinessQuestions] = useState<
    BusinessQuestionEntry[]
  >([]);
  const [isLoadingBusinessQuestions, setIsLoadingBusinessQuestions] =
    useState(false);
  const [newQuestion, setNewQuestion] = useState("");
  const [generateCqMessage, setGenerateCqMessage] = useState<string | null>(
    null
  );
  const [isGeneratingCqs, setIsGeneratingCqs] = useState(false);
  // Mirrors the initiative's currentStageName so the Generate button label and CQ list
  // visibility follow the real backend lifecycle stage.
  const [cqStage, setCqStage] = useState<string | null>(null);
  const [summaryKpis, setSummaryKpis] = useState<GeneratedCqKpi[]>([]);
  // Total competency question records returned by the backend; sections stay hidden while this is 0.
  const [totalRecords, setTotalRecords] = useState(0);

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState("");

  const [businessQuestionsOpen, setBusinessQuestionsOpen] = useState(true);
  const [generatedQuestionsOpen, setGeneratedQuestionsOpen] = useState(true);

  const [rows, setRows] = useState<CompetencyQuestionRow[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [viewDrawerRow, setViewDrawerRow] =
    useState<CompetencyQuestionRow | null>(null);
  const [editDrawerRow, setEditDrawerRow] =
    useState<CompetencyQuestionRow | null>(null);
  const [editForm, setEditForm] = useState<CqEditForm | null>(null);
  const [highlightedRowId, setHighlightedRowId] = useState<string | null>(null);
  const [rowPendingDelete, setRowPendingDelete] =
    useState<CompetencyQuestionRow | null>(null);
  const [bqPendingDelete, setBqPendingDelete] = useState<{
    index: number;
    entry: BusinessQuestionEntry;
  } | null>(null);
  const [businessQuestionsPage, setBusinessQuestionsPage] = useState(1);
  // Backend-paginated CQ table state.
  const [cqPage, setCqPage] = useState(1);
  const [cqTotalPages, setCqTotalPages] = useState(1);
  const [isLoadingMoreRows, setIsLoadingMoreRows] = useState(false);
  // True while "select all" is active, so rows loaded afterwards via scroll get auto-selected too.
  const [selectAllActive, setSelectAllActive] = useState(false);
  // Applied Source/Status filters for the CQ table, plus staged copies bound to the popup
  // checkboxes (only committed when the user clicks "Done").
  const [sourceFilters, setSourceFilters] = useState<SourceCategory[]>([]);
  const [statusFilters, setStatusFilters] = useState<CqStatus[]>([]);
  const [draftSourceFilters, setDraftSourceFilters] = useState<
    SourceCategory[]
  >([]);
  const [draftStatusFilters, setDraftStatusFilters] = useState<CqStatus[]>([]);
  // Business Question codes to filter the CQ table by (client-side only; see applyExtraFilters).
  const [bqIdFilters, setBqIdFilters] = useState<string[]>([]);
  const [draftBqIdFilters, setDraftBqIdFilters] = useState<string[]>([]);
  const [showFilterPopup, setShowFilterPopup] = useState(false);
  const filterPopupRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(filterPopupRef, () => setShowFilterPopup(false));

  const [isAddCqDrawerOpen, setIsAddCqDrawerOpen] = useState(false);
  const [addCqBqIndex, setAddCqBqIndex] = useState<number | null>(null);
  const [addCqQuestionText, setAddCqQuestionText] = useState("");
  const [isSavingAddCq, setIsSavingAddCq] = useState(false);
  const [addCqError, setAddCqError] = useState<string | null>(null);
  // True while the entities-generation trigger POST is in flight after clicking Continue.
  const [isTriggeringEntities, setIsTriggeringEntities] = useState(false);
  const [continueError, setContinueError] = useState<string | null>(null);

  const businessQuestionsTotalPages = Math.max(
    1,
    Math.ceil(businessQuestions.length / BUSINESS_QUESTIONS_PAGE_SIZE)
  );

  // Keep the current page in range whenever questions are added/removed.
  useEffect(() => {
    setBusinessQuestionsPage((prev) =>
      Math.min(prev, businessQuestionsTotalPages)
    );
  }, [businessQuestionsTotalPages]);

  // Fetches the next backend page of CQ rows (honouring the applied Source/Status/BQ ID filters)
  // and appends them; auto-selects the new rows too when "select all" is active, without extra calls.
  const loadMoreRows = async () => {
    if (!initiativeId || isLoadingMoreRows || cqPage >= cqTotalPages) return;
    setIsLoadingMoreRows(true);
    try {
      const nextPage = await OntologyService.getCompetencyQuestions(
        initiativeId,
        cqPage + 1,
        CQ_TABLE_PAGE_SIZE,
        sourceFilters.map((category) => SOURCE_CATEGORY_QUERY_VALUE[category]),
        statusFilters,
        bqIdFilters
      );
      setRows((prev) => [...prev, ...nextPage.rows]);
      if (selectAllActive) {
        setSelectedIds((prev) => [
          ...prev,
          ...nextPage.rows.map((row) => row.id)
        ]);
      }
      setCqPage(nextPage.page);
      setCqTotalPages(nextPage.totalPages);
      setTotalRecords(nextPage.totalRecords);
    } finally {
      setIsLoadingMoreRows(false);
    }
  };

  // Reveals the next backend page once the user scrolls near the bottom of the table.
  const handleRowsScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 80) {
      void loadMoreRows();
    }
  };

  // Hydrate previously-saved business questions for this initiative from the real backend.
  useEffect(() => {
    if (!initiativeId) return;
    let isMounted = true;
    setIsLoadingBusinessQuestions(true);
    OntologyService.getOntologyBusinessQuestions(initiativeId).then(
      (questions) => {
        if (!isMounted) return;
        setBusinessQuestions(
          questions.map((item) => ({
            id: item.businessQuestionId,
            question: item.questionText,
            bqCode: item.bqCode
          }))
        );
        setIsLoadingBusinessQuestions(false);
      }
    );
    return () => {
      isMounted = false;
    };
  }, [initiativeId]);

  // Distinct BQ codes available to filter by, derived from the loaded business questions.
  const bqCodeOptions = useMemo(
    () =>
      Array.from(
        new Set(
          businessQuestions
            .map((item) => item.bqCode)
            .filter((code): code is string => Boolean(code))
        )
      ),
    [businessQuestions]
  );

  const hasExtraFilters =
    sourceFilters.length > 0 ||
    statusFilters.length > 0 ||
    bqIdFilters.length > 0;
  const [isApplyingFilters, setIsApplyingFilters] = useState(false);

  const toggleSourceFilter = (category: SourceCategory) => {
    setDraftSourceFilters((prev) =>
      prev.includes(category)
        ? prev.filter((item) => item !== category)
        : [...prev, category]
    );
  };

  const toggleStatusFilter = (status: CqStatus) => {
    setDraftStatusFilters((prev) =>
      prev.includes(status)
        ? prev.filter((item) => item !== status)
        : [...prev, status]
    );
  };

  const toggleBqIdFilter = (bqCode: string) => {
    setDraftBqIdFilters((prev) =>
      prev.includes(bqCode)
        ? prev.filter((item) => item !== bqCode)
        : [...prev, bqCode]
    );
  };

  // Discards any unsaved checkbox changes and reloads the popup from the currently applied filters.
  const openFilterPopup = () => {
    setDraftSourceFilters(sourceFilters);
    setDraftStatusFilters(statusFilters);
    setDraftBqIdFilters(bqIdFilters);
    setShowFilterPopup(true);
  };

  // Clears the staged (not-yet-applied) selections; still requires "Done" to take effect.
  const clearExtraFilters = () => {
    setDraftSourceFilters([]);
    setDraftStatusFilters([]);
    setDraftBqIdFilters([]);
  };

  // Commits the staged Source/Status/BQ ID selections by re-fetching page 1 from the backend
  // with them applied as query params (?source=, ?status=, ?bq=), rather than filtering the
  // already-loaded rows client-side.
  const applyExtraFilters = async () => {
    if (!initiativeId) return;
    setIsApplyingFilters(true);
    try {
      const firstPage = await OntologyService.getCompetencyQuestions(
        initiativeId,
        1,
        CQ_TABLE_PAGE_SIZE,
        draftSourceFilters.map(
          (category) => SOURCE_CATEGORY_QUERY_VALUE[category]
        ),
        draftStatusFilters,
        draftBqIdFilters
      );
      setRows(firstPage.rows);
      setSummaryKpis(firstPage.summaryKpis);
      setTotalRecords(firstPage.totalRecords);
      setCqPage(firstPage.page);
      setCqTotalPages(firstPage.totalPages);
      setSelectedIds([]);
      setSelectAllActive(false);
      setSourceFilters(draftSourceFilters);
      setStatusFilters(draftStatusFilters);
      setBqIdFilters(draftBqIdFilters);
      setShowFilterPopup(false);
    } finally {
      setIsApplyingFilters(false);
    }
  };

  const allSelected =
    rows.length > 0 && rows.every((row) => selectedIds.includes(row.id));

  // Shown for the normal CQ_GENERATED stage, and also for Entity Generation Failed so the
  // user can still review/retry the already-generated competency questions from this screen.
  const showCqSections =
    cqStage === CQ_GENERATED_STAGE ||
    cqStage === ENTITY_GENERATION_FAILED_STAGE;

  const canContinue = useMemo(
    () =>
      rows.length > 0 &&
      rows.every(
        (row) => row.status === "Approved" || row.status === "Rejected"
      ),
    [rows]
  );

  // Hydrates the initiative's current CQ lifecycle stage on load; resumes polling if
  // generation was already in progress, or fetches the CQ list if already generated.
  useEffect(() => {
    if (!initiativeId) return;
    let isMounted = true;
    (async () => {
      const initiative = await OntologyService.getInitiativeById(initiativeId);
      if (!isMounted) return;
      setCqStage(initiative.currentStageName);

      if (initiative.currentStageName === CQ_GENERATION_IN_PROGRESS_STAGE) {
        const finalStage = await waitForCqGeneration(initiativeId);
        if (!isMounted) return;
        setCqStage(finalStage);
        if (finalStage === CQ_GENERATION_FAILED_STAGE) {
          setGenerateCqMessage(
            "Competency question generation failed. Please try again."
          );
          return;
        }
        const firstPage = await OntologyService.getCompetencyQuestions(
          initiativeId,
          1,
          CQ_TABLE_PAGE_SIZE
        );
        if (!isMounted) return;
        setRows(firstPage.rows);
        setSummaryKpis(firstPage.summaryKpis);
        setTotalRecords(firstPage.totalRecords);
        setCqPage(firstPage.page);
        setCqTotalPages(firstPage.totalPages);
      } else if (initiative.currentStageName === CQ_GENERATED_STAGE) {
        const firstPage = await OntologyService.getCompetencyQuestions(
          initiativeId,
          1,
          CQ_TABLE_PAGE_SIZE
        );
        if (!isMounted) return;
        setRows(firstPage.rows);
        setSummaryKpis(firstPage.summaryKpis);
        setTotalRecords(firstPage.totalRecords);
        setCqPage(firstPage.page);
        setCqTotalPages(firstPage.totalPages);
      } else if (
        initiative.currentStageName === ENTITY_GENERATION_FAILED_STAGE
      ) {
        const firstPage = await OntologyService.getCompetencyQuestions(
          initiativeId,
          1,
          CQ_TABLE_PAGE_SIZE
        );
        if (!isMounted) return;
        setRows(firstPage.rows);
        setSummaryKpis(firstPage.summaryKpis);
        setTotalRecords(firstPage.totalRecords);
        setCqPage(firstPage.page);
        setCqTotalPages(firstPage.totalPages);
      } else if (initiative.currentStageName === CQ_GENERATION_FAILED_STAGE) {
        setGenerateCqMessage(
          "Competency question generation failed. Please try again."
        );
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [initiativeId]);

  const addBusinessQuestion = () => {
    const trimmed = newQuestion.trim();
    if (!trimmed) return;
    setNewQuestion("");
    const tempEntry: BusinessQuestionEntry = {
      id: null,
      question: trimmed,
      bqCode: null
    };
    setBusinessQuestions((prev) => [...prev, tempEntry]);
    if (initiativeId) {
      OntologyService.addOntologyBusinessQuestion(initiativeId, trimmed).then(
        (created) => {
          setBusinessQuestions((prev) =>
            prev.map((item) =>
              item === tempEntry
                ? {
                    id: created.businessQuestionId,
                    question: created.questionText,
                    bqCode: created.bqCode
                  }
                : item
            )
          );
        }
      );
    }
  };

  const handleGenerateCqsClick = async () => {
    if (businessQuestions.length < 1) {
      setGenerateCqMessage("Please add at least 1 business question.");
      return;
    }
    setGenerateCqMessage(null);

    if (!initiativeId) {
      // No initiative context yet (e.g. unsaved draft) — fall back to the client-side dummy preview.
      setSummaryKpis(DUMMY_GENERATED_CQ_KPIS);
      setTotalRecords(
        DUMMY_GENERATED_CQ_KPIS.find((kpi) => kpi.id === "total-generated")
          ?.value ?? 0
      );
      return;
    }

    setIsGeneratingCqs(true);
    setCqStage(CQ_GENERATION_IN_PROGRESS_STAGE);
    try {
      // generationMode disabled for now; backend always runs a full generation.
      await OntologyService.triggerCompetencyQuestionGeneration(initiativeId);
      const finalStage = await waitForCqGeneration(initiativeId);
      setCqStage(finalStage);

      if (finalStage === CQ_GENERATION_FAILED_STAGE) {
        setGenerateCqMessage(
          "Competency question generation failed. Please try again."
        );
        return;
      }

      const firstPage = await OntologyService.getCompetencyQuestions(
        initiativeId,
        1,
        CQ_TABLE_PAGE_SIZE
      );
      setRows(firstPage.rows);
      setSummaryKpis(firstPage.summaryKpis);
      setTotalRecords(firstPage.totalRecords);
      setCqPage(firstPage.page);
      setCqTotalPages(firstPage.totalPages);
    } catch {
      setGenerateCqMessage(
        "Failed to trigger competency question generation. Please try again."
      );
    } finally {
      setIsGeneratingCqs(false);
    }
  };

  const startEditQuestion = (index: number) => {
    setEditingIndex(index);
    setEditingValue(businessQuestions[index].question);
  };

  // Persists the edited text to the backend (PUT) when the question already has an id.
  const saveEditQuestion = () => {
    if (editingIndex === null) return;
    const index = editingIndex;
    const trimmedValue = editingValue.trim();
    const current = businessQuestions[index];
    setEditingIndex(null);
    if (!trimmedValue || trimmedValue === current.question) return;
    setBusinessQuestions((prev) =>
      prev.map((item, itemIndex) =>
        itemIndex === index ? { ...item, question: trimmedValue } : item
      )
    );
    if (initiativeId && current.id) {
      OntologyService.updateOntologyBusinessQuestion(
        initiativeId,
        current.id,
        trimmedValue
      );
    }
  };

  const cancelEditQuestion = () => {
    setEditingIndex(null);
  };

  // Removes a business question locally and deletes it from the backend when it has an id.
  const removeBusinessQuestion = (index: number) => {
    const current = businessQuestions[index];
    setBusinessQuestions((prev) =>
      prev.filter((_, itemIndex) => itemIndex !== index)
    );
    if (editingIndex === index) setEditingIndex(null);
    if (initiativeId && current.id) {
      OntologyService.deleteOntologyBusinessQuestion(initiativeId, current.id);
    }
  };

  const askDeleteBq = (index: number) => {
    setBqPendingDelete({ index, entry: businessQuestions[index] });
  };

  const cancelDeleteBq = () => {
    setBqPendingDelete(null);
  };

  const confirmDeleteBq = () => {
    if (!bqPendingDelete) return;
    removeBusinessQuestion(bqPendingDelete.index);
    setBqPendingDelete(null);
  };

  // Selects every currently loaded competency question; no extra API call is made, but rows
  // loaded afterwards via infinite scroll are auto-selected too while active.
  const toggleSelectAll = () => {
    const visibleIds = rows.map((row) => row.id);
    if (allSelected) {
      setSelectAllActive(false);
      setSelectedIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
      return;
    }
    setSelectAllActive(true);
    setSelectedIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
  };

  const toggleSelectRow = (id: string) => {
    setSelectAllActive(false);
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );
  };

  const setRowStatus = (id: string, status: CqStatus) => {
    setRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, status } : row))
    );
  };

  const applyBulkStatus = (status: CqStatus) => {
    const idsToUpdate = selectedIds;
    setRows((prev) =>
      prev.map((row) =>
        idsToUpdate.includes(row.id) ? { ...row, status } : row
      )
    );
    setSelectedIds([]);
    if (idsToUpdate.length === 0) return;
    if (status === "Approved") {
      OntologyService.acceptCompetencyQuestions(idsToUpdate);
    } else if (status === "Rejected") {
      OntologyService.rejectCompetencyQuestions(idsToUpdate);
    }
  };

  const removeRow = (id: string) => {
    setRows((prev) => prev.filter((row) => row.id !== id));
    setSelectedIds((prev) => prev.filter((rowId) => rowId !== id));
    if (highlightedRowId === id) setHighlightedRowId(null);
    if (viewDrawerRow?.id === id) setViewDrawerRow(null);
    if (editDrawerRow?.id === id) closeEditDrawer();
    setTotalRecords((prev) => Math.max(0, prev - 1));
    OntologyService.deleteCompetencyQuestion(id);
  };

  const askDeleteRow = (row: CompetencyQuestionRow) => {
    setRowPendingDelete(row);
  };

  const cancelDeleteRow = () => {
    setRowPendingDelete(null);
  };

  const confirmDeleteRow = () => {
    if (!rowPendingDelete) return;
    removeRow(rowPendingDelete.id);
    setRowPendingDelete(null);
  };

  const openAddCqDrawer = () => {
    setAddCqBqIndex(null);
    setAddCqQuestionText("");
    setAddCqError(null);
    setIsAddCqDrawerOpen(true);
  };

  const closeAddCqDrawer = () => {
    setIsAddCqDrawerOpen(false);
    setAddCqBqIndex(null);
    setAddCqQuestionText("");
    setAddCqError(null);
  };

  // Manually creates a single competency question for the selected business question via
  // POST /initiatives/{initiativeId}/business-questions/{businessQuestionId}/competency-questions.
  const saveAddCqDrawer = async () => {
    if (addCqBqIndex === null) return;
    const trimmed = addCqQuestionText.trim();
    if (!trimmed) return;
    const selectedBq = businessQuestions[addCqBqIndex];
    if (!initiativeId || !selectedBq.id) return;

    setAddCqError(null);
    setIsSavingAddCq(true);
    try {
      const newRow = await OntologyService.createCompetencyQuestion(
        initiativeId,
        selectedBq.id,
        trimmed
      );
      const updatedRows = [...rows, newRow];
      setRows(updatedRows);
      // Bump the backend-sourced summary locally rather than re-deriving it from rows.
      const newRowKpiId = CQ_STATUS_KPI_ID[newRow.status];
      setSummaryKpis((prev) =>
        prev.map((kpi) =>
          kpi.id === "total-generated" || kpi.id === newRowKpiId
            ? { ...kpi, value: kpi.value + 1 }
            : kpi
        )
      );
      setTotalRecords((prev) => prev + 1);
      closeAddCqDrawer();
    } catch {
      setAddCqError("Failed to add competency question. Please try again.");
    } finally {
      setIsSavingAddCq(false);
    }
  };

  const openEditDrawer = (row: CompetencyQuestionRow) => {
    setHighlightedRowId(row.id);
    setEditDrawerRow(row);
    setEditForm({
      cqId: row.id,
      initiativeId: row.initiativeId,
      businessQuestionId: row.businessQuestionId,
      businessQuestionCode: row.businessQuestionCode,
      businessQuestionText: row.businessQuestionText,
      cqCode: row.cqId,
      questionText: row.question,
      confidenceScore: row.confidenceScore,
      status: row.status,
      explanation: row.explanation,
      evidenceSource: row.evidenceSource,
      metadata: row.metadata,
      createdAt: row.createdAt,
      updatedAt: row.updatedAt,
      reviewedAt: row.reviewedAt
    });
  };

  const closeEditDrawer = () => {
    setEditDrawerRow(null);
    setEditForm(null);
    setHighlightedRowId(null);
  };

  const updateEditForm = <K extends keyof CqEditForm>(
    key: K,
    value: CqEditForm[K]
  ) => {
    setEditForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  // Persists the edited question text via PUT; businessQuestionText/explanation stay client-side
  // only since the backend update endpoint only accepts questionText. Once the save succeeds,
  // the row's source is refreshed too (e.g. AI-generated questions flip to "AI + EDITED").
  const saveEditDrawer = async () => {
    if (!editDrawerRow || !editForm) return;

    if (!initiativeId) {
      setRows((prev) =>
        prev.map((row) =>
          row.id === editDrawerRow.id
            ? {
                ...row,
                question: editForm.questionText,
                businessQuestionText: editForm.businessQuestionText,
                explanation: editForm.explanation,
                updatedAt: new Date().toISOString()
              }
            : row
        )
      );
      closeEditDrawer();
      return;
    }

    try {
      const updatedRow = await OntologyService.updateCompetencyQuestion(
        initiativeId,
        editDrawerRow.id,
        editForm.questionText
      );
      setRows((prev) =>
        prev.map((row) =>
          row.id === editDrawerRow.id
            ? {
                ...row,
                question: editForm.questionText,
                businessQuestionText: editForm.businessQuestionText,
                explanation: editForm.explanation,
                source: updatedRow.source,
                updatedAt: updatedRow.updatedAt
              }
            : row
        )
      );
      closeEditDrawer();
    } catch {
      // Keep the drawer open on failure so the user can retry.
    }
  };

  const closeViewDrawer = () => {
    setViewDrawerRow(null);
    setHighlightedRowId(null);
  };

  // Opens the View drawer using the row already loaded from the competency questions list.
  const openViewDrawer = (row: CompetencyQuestionRow) => {
    setHighlightedRowId(row.id);
    setViewDrawerRow(row);
  };

  const handleContinue = async () => {
    if (!canContinue) return;
    if (!initiativeId) {
      navigate("/ontology-studio/create/entities-relationships", {
        state: { initiativeId }
      });
      return;
    }
    setContinueError(null);
    setIsTriggeringEntities(true);
    try {
      await OntologyService.triggerEntitiesGeneration(initiativeId);
      navigate("/ontology-studio/create/entities-relationships", {
        state: { initiativeId }
      });
    } catch {
      setContinueError(
        "We couldn't start entity generation. Please try again."
      );
    } finally {
      setIsTriggeringEntities(false);
    }
  };

  return (
    <div className="create-questions-container">
      <nav className="create-questions-breadcrumb">
        <Link to="/home" className="breadcrumb-link">
          Engineering Information Management
        </Link>
        <span className="breadcrumb-separator">&gt;</span>
        <Link to="/create-ontology" className="breadcrumb-link">
          Ontology Studio
        </Link>
        <span className="breadcrumb-separator">&gt;</span>
        <span className="breadcrumb-current">Ontology Workbench</span>
      </nav>

      <div className="create-questions-header">
        <div className="create-questions-header__title-row">
          <h1 className="create-questions-title">Ontology Workbench</h1>
          <span
            className="create-questions-help-badge"
            title="Ontology workbench help"
          >
            ?
          </span>
        </div>
        <p className="create-questions-description">
          A structured workspace to elicit, generate, and govern competency
          questions that drive your ontology requirements - from business
          questions through to validated model artifacts.
        </p>
        <div className="create-questions-meta">
          <span className="create-questions-meta__pill">
            Initiative ID <strong>{initiativeId ?? "EY-ONT-2026-TEMP"}</strong>
          </span>
          <span className="create-questions-meta__pill">
            Status <strong>Draft</strong>
          </span>
        </div>
      </div>

      <div className="create-questions-workflow">
        {ONTOLOGY_WORKBENCH_STEPS.map((step) => (
          <div
            key={step.id}
            className={`create-questions-workflow__step${
              step.id === 2 ? " create-questions-workflow__step--active" : ""
            }${step.id < 2 ? " create-questions-workflow__step--done" : ""}`}
          >
            <span className="create-questions-workflow__indicator">
              {step.id < 2 ? (
                <CheckCircleOutlined fontSize="inherit" />
              ) : step.id === 2 ? (
                "•"
              ) : (
                ""
              )}
            </span>
            <span className="create-questions-workflow__label">
              {step.label}
            </span>
          </div>
        ))}
      </div>

      <section className="create-questions-card">
        <button
          type="button"
          className="create-questions-card__header"
          onClick={() => setBusinessQuestionsOpen((prev) => !prev)}
        >
          <div className="create-questions-card__title-group">
            <h3 className="create-questions-card__title">Business Questions</h3>
            <p className="create-questions-card__subtitle">
              Capture the business problems and decisions the ontology must
              support.
            </p>
          </div>
          <ExpandMoreOutlined
            className={`create-questions-card__chevron${
              businessQuestionsOpen
                ? " create-questions-card__chevron--open"
                : ""
            }`}
          />
        </button>

        {businessQuestionsOpen && (
          <div className="create-questions-card__body">
            <div className="create-questions-input-area">
              <span className="create-questions-input-area__label">
                What questions do you need to answer with this ontology?
              </span>
              <div className="create-questions-input-row">
                <input
                  type="text"
                  className="create-questions-input"
                  placeholder="Type a business question and press Enter or click Add..."
                  value={newQuestion}
                  onChange={(event) => setNewQuestion(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      addBusinessQuestion();
                    }
                  }}
                />
                <button
                  type="button"
                  className="create-questions-btn create-questions-btn--primary"
                  disabled={isLoadingBusinessQuestions}
                  onClick={addBusinessQuestion}
                >
                  + Add Question
                </button>
              </div>
            </div>

            <div className="create-questions-bq-section">
              <span className="create-questions-bq-section__label">
                Active Business Questions
              </span>
              {isLoadingBusinessQuestions ? (
                <div className="create-questions-bq-loading">
                  <Spinner size={20} />
                </div>
              ) : businessQuestions.length === 0 ? (
                <p className="create-questions-bq-placeholder">
                  Please add some business questions.
                </p>
              ) : (
                <>
                  <ul className="create-questions-bq-list">
                    {businessQuestions
                      .slice(
                        (businessQuestionsPage - 1) *
                          BUSINESS_QUESTIONS_PAGE_SIZE,
                        businessQuestionsPage * BUSINESS_QUESTIONS_PAGE_SIZE
                      )
                      .map((question, pageIndex) => {
                        const index =
                          (businessQuestionsPage - 1) *
                            BUSINESS_QUESTIONS_PAGE_SIZE +
                          pageIndex;
                        return (
                          <li
                            key={question.id ?? index}
                            className="create-questions-bq-row"
                          >
                            <span className="create-questions-bq-row__number">
                              {index + 1}
                            </span>
                            {editingIndex === index ? (
                              <input
                                type="text"
                                className="create-questions-bq-row__edit-input"
                                value={editingValue}
                                autoFocus
                                onChange={(event) =>
                                  setEditingValue(event.target.value)
                                }
                                onKeyDown={(event) => {
                                  if (event.key === "Enter") saveEditQuestion();
                                  if (event.key === "Escape")
                                    cancelEditQuestion();
                                }}
                              />
                            ) : (
                              <span className="create-questions-bq-row__text">
                                {question.question}
                              </span>
                            )}
                            {editingIndex === index ? (
                              <div className="create-questions-row-actions">
                                <button
                                  type="button"
                                  className="create-questions-icon-btn create-questions-icon-btn--approve"
                                  aria-label="Approve question"
                                  onClick={saveEditQuestion}
                                >
                                  <CheckOutlined fontSize="small" />
                                </button>
                                <button
                                  type="button"
                                  className="create-questions-icon-btn create-questions-icon-btn--reject"
                                  aria-label="Cancel edit"
                                  onClick={cancelEditQuestion}
                                >
                                  <CloseOutlined fontSize="small" />
                                </button>
                              </div>
                            ) : (
                              <div className="create-questions-row-actions">
                                <button
                                  type="button"
                                  className="create-questions-icon-btn"
                                  aria-label="Edit question"
                                  onClick={() => startEditQuestion(index)}
                                >
                                  <EditOutlined fontSize="small" />
                                </button>
                                <button
                                  type="button"
                                  className="create-questions-icon-btn"
                                  aria-label="Delete question"
                                  onClick={() => askDeleteBq(index)}
                                >
                                  <DeleteOutline fontSize="small" />
                                </button>
                              </div>
                            )}
                          </li>
                        );
                      })}
                  </ul>
                  <div className="create-questions-bq-footer">
                    <span className="create-questions-bq-footer__info">
                      Showing{" "}
                      {businessQuestions.length === 0
                        ? 0
                        : (businessQuestionsPage - 1) *
                            BUSINESS_QUESTIONS_PAGE_SIZE +
                          1}
                      -
                      {Math.min(
                        businessQuestionsPage * BUSINESS_QUESTIONS_PAGE_SIZE,
                        businessQuestions.length
                      )}{" "}
                      of {businessQuestions.length} business questions
                    </span>
                    {businessQuestions.length >
                      BUSINESS_QUESTIONS_PAGE_SIZE && (
                      <SimplePagination
                        currentPage={businessQuestionsPage}
                        totalPages={businessQuestionsTotalPages}
                        onPageChange={setBusinessQuestionsPage}
                      />
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Always visible, independent of the Business Questions list's collapsed state. */}
        <div className="create-questions-card__body">
          <div className="create-questions-generate-banner">
            <AutoAwesomeOutlined
              fontSize="small"
              className="create-questions-generate-banner__icon"
            />
            <span className="create-questions-generate-banner__text">
              Happy with your business questions? Generate competency questions
              and concepts from them.
            </span>
            <button
              type="button"
              className={`create-questions-btn create-questions-btn--dark${
                businessQuestions.length < 1
                  ? " create-questions-btn--deactivated"
                  : ""
              }`}
              aria-disabled={
                businessQuestions.length < 1 ||
                isGeneratingCqs ||
                cqStage === CQ_GENERATION_IN_PROGRESS_STAGE
              }
              disabled={
                isGeneratingCqs || cqStage === CQ_GENERATION_IN_PROGRESS_STAGE
              }
              onClick={handleGenerateCqsClick}
            >
              {isGeneratingCqs || cqStage === CQ_GENERATION_IN_PROGRESS_STAGE
                ? "Generating\u2026"
                : "Generate CQs & Concepts \u2192"}
            </button>
          </div>
          {generateCqMessage && (
            <span className="create-questions-generate-banner__error">
              {generateCqMessage}
            </span>
          )}
          {(isGeneratingCqs || cqStage === CQ_GENERATION_IN_PROGRESS_STAGE) && (
            <CqGenerationShimmer />
          )}
        </div>
      </section>

      {showCqSections && (
        <section className="create-questions-card">
          <button
            type="button"
            className="create-questions-card__header"
            onClick={() => setGeneratedQuestionsOpen((prev) => !prev)}
          >
            <div className="create-questions-card__title-group">
              <h3 className="create-questions-card__title">
                Generated Competency Questions
              </h3>
              <p className="create-questions-card__subtitle">
                Generate formal questions that drive ontology requirements.
              </p>
            </div>
            <ExpandMoreOutlined
              className={`create-questions-card__chevron${
                generatedQuestionsOpen
                  ? " create-questions-card__chevron--open"
                  : ""
              }`}
            />
          </button>

          {generatedQuestionsOpen && (
            <div className="create-questions-card__body">
              <div className="create-questions-summary-row">
                {summaryKpis.map((kpi) => (
                  <div
                    key={kpi.id}
                    className={`create-questions-summary-card${
                      SUMMARY_CARD_CLASS[kpi.id]
                        ? ` ${SUMMARY_CARD_CLASS[kpi.id]}`
                        : ""
                    }`}
                  >
                    <span className="create-questions-summary-card__label">
                      {kpi.label}
                    </span>
                    <span className="create-questions-summary-card__value">
                      {kpi.value}
                    </span>
                  </div>
                ))}
              </div>

              {/* <div className="create-questions-alerts">
              {CQ_ALERTS.map((alert) => (
                <div
                  key={alert.id}
                  className={`create-questions-alert create-questions-alert--${alert.tone}`}
                >
                  <span className="create-questions-alert__icon">
                    {ALERT_ICONS[alert.tone]}
                  </span>
                  <span className="create-questions-alert__text">
                    {alert.text}
                  </span>
                  <button type="button" className="create-questions-alert__link">
                    View Details →
                  </button>
                </div>
              ))}
            </div> */}
            </div>
          )}
        </section>
      )}

      {showCqSections && (
        <section className="create-questions-card">
          <div className="create-questions-card__header create-questions-card__header--static">
            <div className="create-questions-card__title-group">
              <h3 className="create-questions-card__title">
                Competency Question Table
              </h3>
              <p className="create-questions-card__subtitle">
                Review, approve, reject, and edit generated competency
                questions.
              </p>
            </div>
            <div className="create-questions-card__header-actions">
              <button
                type="button"
                className="create-questions-btn create-questions-btn--approve"
                disabled={selectedIds.length === 0}
                onClick={() => applyBulkStatus("Approved")}
              >
                <CheckOutlined fontSize="small" />
                Approve Selected
              </button>
              <button
                type="button"
                className="create-questions-btn create-questions-btn--reject"
                disabled={selectedIds.length === 0}
                onClick={() => applyBulkStatus("Rejected")}
              >
                <CloseOutlined fontSize="small" />
                Reject Selected
              </button>
              <div
                className="create-questions-filter-popup-anchor"
                ref={filterPopupRef}
              >
                <button
                  type="button"
                  className={`create-questions-btn create-questions-btn--secondary${
                    hasExtraFilters ? " create-questions-btn--active" : ""
                  }`}
                  onClick={() =>
                    showFilterPopup
                      ? setShowFilterPopup(false)
                      : openFilterPopup()
                  }
                >
                  <TuneOutlined fontSize="small" />
                  <span>Filters</span>
                  {hasExtraFilters && (
                    <span className="create-questions-filter-count">
                      {sourceFilters.length +
                        statusFilters.length +
                        bqIdFilters.length}
                    </span>
                  )}
                </button>
                {showFilterPopup && (
                  <div className="create-questions-filter-popup">
                    <div className="create-questions-filter-popup__section">
                      <h4 className="create-questions-filter-popup__title">
                        Source
                      </h4>
                      {SOURCE_FILTER_OPTIONS.map((option) => (
                        <label
                          key={option.value}
                          className="create-questions-filter-popup__checkbox"
                        >
                          <input
                            type="checkbox"
                            checked={draftSourceFilters.includes(option.value)}
                            onChange={() => toggleSourceFilter(option.value)}
                          />
                          {option.label}
                        </label>
                      ))}
                    </div>
                    <div className="create-questions-filter-popup__section">
                      <h4 className="create-questions-filter-popup__title">
                        Status
                      </h4>
                      {STATUS_FILTER_OPTIONS.map((status) => (
                        <label
                          key={status}
                          className="create-questions-filter-popup__checkbox"
                        >
                          <input
                            type="checkbox"
                            checked={draftStatusFilters.includes(status)}
                            onChange={() => toggleStatusFilter(status)}
                          />
                          {status}
                        </label>
                      ))}
                    </div>
                    <div className="create-questions-filter-popup__section">
                      <h4 className="create-questions-filter-popup__title">
                        BQ ID
                      </h4>
                      {bqCodeOptions.map((bqCode) => (
                        <label
                          key={bqCode}
                          className="create-questions-filter-popup__checkbox"
                        >
                          <input
                            type="checkbox"
                            checked={draftBqIdFilters.includes(bqCode)}
                            onChange={() => toggleBqIdFilter(bqCode)}
                          />
                          {bqCode}
                        </label>
                      ))}
                    </div>
                    <div className="create-questions-filter-popup__footer">
                      <button
                        type="button"
                        className="create-questions-filter-popup__clear"
                        onClick={clearExtraFilters}
                        disabled={
                          draftSourceFilters.length === 0 &&
                          draftStatusFilters.length === 0 &&
                          draftBqIdFilters.length === 0
                        }
                      >
                        Clear all
                      </button>
                      <button
                        type="button"
                        className="create-questions-filter-popup__done"
                        onClick={applyExtraFilters}
                        disabled={isApplyingFilters}
                      >
                        {isApplyingFilters ? "Applying\u2026" : "Done"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="create-questions-card__body">
            <div className="create-questions-bulk-bar">
              <button
                type="button"
                className={`create-questions-bulk-bar__select${
                  allSelected ? " create-questions-bulk-bar__select--all" : ""
                }`}
                aria-label="Select all"
                onClick={toggleSelectAll}
              >
                {allSelected ? <CheckOutlined fontSize="inherit" /> : "-"}
              </button>
              <span className="create-questions-bulk-bar__label">
                Select all &bull; {selectedIds.length} selected out of{" "}
                {totalRecords}
              </span>
            </div>
            <div
              className="create-questions-table-scroll"
              onScroll={handleRowsScroll}
            >
              <table className="create-questions-table">
                <thead>
                  <tr>
                    <th></th>
                    <th>BQ ID</th>
                    <th>CQ ID</th>
                    <th>Competency Question</th>
                    <th>Confidence Score</th>
                    <th>Source</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <React.Fragment key={row.id}>
                      <tr
                        className={
                          row.id === highlightedRowId
                            ? "create-questions-table__row--needs-review"
                            : undefined
                        }
                      >
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(row.id)}
                            onChange={() => toggleSelectRow(row.id)}
                          />
                        </td>
                        <td className="create-questions-table__cq-id">
                          {row.businessQuestionCode ?? "Not recorded yet"}
                        </td>
                        <td className="create-questions-table__cq-id">
                          {row.cqId}
                        </td>
                        <td className="create-questions-table__question">
                          {row.question}
                        </td>
                        <td>{row.confidence}%</td>
                        <td>
                          {row.source ? (
                            <span
                              className={`create-questions-source-pill ${getSourceClass(
                                row.source
                              )}`}
                            >
                              {row.source}
                            </span>
                          ) : (
                            "Not recorded yet"
                          )}
                        </td>
                        <td>
                          <span
                            className={`create-questions-status-pill ${
                              STATUS_CLASS[row.status]
                            }`}
                          >
                            {row.status}
                          </span>
                        </td>
                        <td>
                          <div className="create-questions-row-actions">
                            <button
                              type="button"
                              className="create-questions-icon-btn"
                              aria-label="View details"
                              onClick={() => openViewDrawer(row)}
                            >
                              <VisibilityOutlined fontSize="small" />
                            </button>
                            <button
                              type="button"
                              className="create-questions-icon-btn"
                              aria-label="Edit question"
                              onClick={() => openEditDrawer(row)}
                            >
                              <EditOutlined fontSize="small" />
                            </button>
                            {/* <button
                          type="button"
                          className="create-questions-icon-btn"
                          aria-label="Delete question"
                          onClick={() => askDeleteRow(row)}
                        >
                          <DeleteOutline fontSize="small" />
                        </button> */}
                          </div>
                        </td>
                      </tr>
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="create-questions-table-footer">
              <span className="create-questions-table-footer__info">
                Showing {rows.length} of {totalRecords} competency questions
              </span>
              {isLoadingMoreRows ? (
                <span className="create-questions-table-footer__info">
                  Loading more…
                </span>
              ) : (
                cqPage < cqTotalPages && (
                  <span className="create-questions-table-footer__info">
                    Scroll down to load more…
                  </span>
                )
              )}
            </div>
          </div>
        </section>
      )}

      <div className="create-questions-footer">
        <div className="create-questions-footer__right">
          {continueError && (
            <span className="create-questions-generate-banner__error">
              {continueError}
            </span>
          )}
          <button
            type="button"
            disabled={!showCqSections}
            className="create-questions-btn create-questions-btn--primary"
            onClick={openAddCqDrawer}
          >
            + Add Competency Question
          </button>
          <button
            type="button"
            className="create-questions-btn create-questions-btn--continue"
            disabled={!canContinue || isTriggeringEntities}
            onClick={handleContinue}
          >
            {isTriggeringEntities ? "Starting\u2026" : "Continue \u2192"}
          </button>
        </div>
      </div>

      {viewDrawerRow && (
        <div className="create-questions-drawer-root">
          <div
            className="create-questions-drawer-overlay"
            onClick={closeViewDrawer}
          />
          <aside className="create-questions-drawer">
            <div className="create-questions-drawer__header">
              <div className="create-questions-drawer__title-group">
                <h2 className="create-questions-drawer__title">
                  Competency Question Details
                </h2>
                <p className="create-questions-drawer__subtitle">
                  Read-only review state for the selected competency question.
                </p>
              </div>
              <div className="create-questions-drawer__header-actions">
                <button
                  type="button"
                  className="create-questions-drawer__close"
                  aria-label="Close"
                  onClick={closeViewDrawer}
                >
                  <CloseOutlined fontSize="small" />
                </button>
              </div>
            </div>

            <div className="create-questions-drawer__body">
              <div className="create-questions-drawer__summary-card">
                <div className="create-questions-drawer__summary-top">
                  <span className="create-questions-drawer__summary-label">
                    Selected Competency Question
                  </span>
                  <span
                    className={`create-questions-status-pill ${
                      STATUS_CLASS[viewDrawerRow.status]
                    }`}
                  >
                    {viewDrawerRow.status}
                  </span>
                </div>
                <p className="create-questions-drawer__summary-question">
                  {viewDrawerRow.question}
                </p>
                <div className="create-questions-drawer__chip-row">
                  <span className="create-questions-drawer__chip create-questions-drawer__chip--highlight">
                    {viewDrawerRow.cqId}
                  </span>
                  <span className="create-questions-drawer__chip">
                    Confidence{" "}
                    {viewDrawerRow.confidenceScore != null
                      ? `${viewDrawerRow.confidenceScore}%`
                      : "Not recorded yet"}
                  </span>
                  <span className="create-questions-drawer__chip">
                    Generated from{" "}
                    {viewDrawerRow.businessQuestionCode ?? "Not recorded yet"}
                  </span>
                </div>
              </div>

              <div className="create-questions-drawer__section">
                <div className="create-questions-drawer__field">
                  <span className="create-questions-drawer__field-label">
                    Business Question
                  </span>
                  <span className="create-questions-drawer__field-value">
                    {viewDrawerRow.businessQuestionText || "Not recorded yet"}
                  </span>
                </div>
              </div>

              <div className="create-questions-drawer__section">
                <div className="create-questions-drawer__field-row"></div>
                <div className="create-questions-drawer__field-row">
                  <div className="create-questions-drawer__field create-questions-drawer__field--flex">
                    <span className="create-questions-drawer__field-label">
                      Generated
                    </span>
                    <span className="create-questions-drawer__field-value">
                      {formatDateTime(viewDrawerRow.createdAt)}
                    </span>
                  </div>
                  <div className="create-questions-drawer__field create-questions-drawer__field--flex">
                    <span className="create-questions-drawer__field-label">
                      Last Edited
                    </span>
                    <span className="create-questions-drawer__field-value">
                      {formatDateTime(viewDrawerRow.updatedAt)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </aside>
        </div>
      )}

      {editDrawerRow && editForm && (
        <div className="create-questions-drawer-root">
          <div
            className="create-questions-drawer-overlay"
            onClick={closeEditDrawer}
          />
          <aside className="create-questions-drawer">
            <div className="create-questions-drawer__header">
              <div className="create-questions-drawer__title-group">
                <h2 className="create-questions-drawer__title">
                  Edit Competency Question
                </h2>
                <p className="create-questions-drawer__subtitle">
                  Review and refine the selected competency question before
                  saving.
                </p>
              </div>
              <button
                type="button"
                className="create-questions-drawer__close"
                aria-label="Close"
                onClick={closeEditDrawer}
              >
                <CloseOutlined fontSize="small" />
              </button>
            </div>

            <div className="create-questions-drawer__body">
              <div className="create-questions-drawer__field">
                <span className="create-questions-drawer__field-label">
                  CQ ID
                </span>
                <input
                  type="text"
                  className="create-questions-drawer__input"
                  value={editForm.cqCode}
                  readOnly
                />
              </div>
              <div className="create-questions-drawer__field">
                <span className="create-questions-drawer__field-label">
                  Competency Question
                </span>
                <textarea
                  className="create-questions-drawer__input"
                  value={editForm.questionText}
                  onChange={(event) =>
                    updateEditForm("questionText", event.target.value)
                  }
                />
              </div>
              <div className="create-questions-drawer__field">
                <span className="create-questions-drawer__field-label">
                  BQ ID
                </span>
                <input
                  type="text"
                  className="create-questions-drawer__input"
                  value={editDrawerRow.businessQuestionCode ?? ""}
                  readOnly
                />
              </div>
              <div className="create-questions-drawer__field">
                <span className="create-questions-drawer__field-label">
                  Business Question
                </span>
                <textarea
                  className="create-questions-drawer__input"
                  placeholder="Not recorded yet"
                  value={editForm.businessQuestionText ?? ""}
                  readOnly
                  onChange={(event) =>
                    updateEditForm("businessQuestionText", event.target.value)
                  }
                />
              </div>
              <div className="create-questions-drawer__field">
                <span className="create-questions-drawer__field-label">
                  Status
                </span>
                <input
                  type="text"
                  className="create-questions-drawer__input"
                  value={editForm.status}
                  readOnly
                />
              </div>
              <div className="create-questions-drawer__field">
                <span className="create-questions-drawer__field-label">
                  Notes
                </span>
                <textarea
                  className="create-questions-drawer__textarea"
                  rows={4}
                  placeholder="Not recorded yet"
                  value={editForm.explanation ?? ""}
                  onChange={(event) =>
                    updateEditForm("explanation", event.target.value)
                  }
                />
              </div>
            </div>

            <div className="create-questions-drawer__footer">
              <button
                type="button"
                className="create-questions-btn create-questions-btn--secondary"
                onClick={closeEditDrawer}
              >
                Cancel
              </button>
              <button
                type="button"
                className="create-questions-btn create-questions-btn--primary"
                onClick={saveEditDrawer}
              >
                Save Changes
              </button>
            </div>
          </aside>
        </div>
      )}

      {isAddCqDrawerOpen && (
        <div className="create-questions-drawer-root">
          <div
            className="create-questions-drawer-overlay"
            onClick={closeAddCqDrawer}
          />
          <aside className="create-questions-drawer">
            <div className="create-questions-drawer__header">
              <div className="create-questions-drawer__title-group">
                <h2 className="create-questions-drawer__title">
                  Add Competency Question
                </h2>
                <p className="create-questions-drawer__subtitle">
                  Select a business question and add a new competency question
                  for it.
                </p>
              </div>
              <button
                type="button"
                className="create-questions-drawer__close"
                aria-label="Close"
                onClick={closeAddCqDrawer}
              >
                <CloseOutlined fontSize="small" />
              </button>
            </div>

            <div className="create-questions-drawer__body">
              <div className="create-questions-drawer__field">
                <span className="create-questions-drawer__field-label">
                  Business Question
                </span>
                <select
                  className="create-questions-drawer__input"
                  value={addCqBqIndex ?? ""}
                  onChange={(event) =>
                    setAddCqBqIndex(
                      event.target.value === ""
                        ? null
                        : Number(event.target.value)
                    )
                  }
                >
                  <option value="" disabled>
                    Select a business question…
                  </option>
                  {businessQuestions.map((question, index) => (
                    <option key={question.id ?? index} value={index}>
                      {`BQ-${index + 1}`}
                    </option>
                  ))}
                </select>
              </div>
              <div className="create-questions-drawer__field">
                <span className="create-questions-drawer__field-label">
                  Business Question Text
                </span>
                <textarea
                  className="create-questions-drawer__textarea"
                  rows={4}
                  value={
                    addCqBqIndex !== null
                      ? businessQuestions[addCqBqIndex].question
                      : ""
                  }
                  placeholder="Select a business question above"
                  readOnly
                />
              </div>
              <div className="create-questions-drawer__field">
                <span className="create-questions-drawer__field-label">
                  Competency Question
                </span>
                <textarea
                  className="create-questions-drawer__textarea"
                  rows={4}
                  placeholder="Type the new competency question…"
                  value={addCqQuestionText}
                  onChange={(event) => setAddCqQuestionText(event.target.value)}
                />
              </div>
              {addCqError && (
                <span className="create-questions-generate-banner__error">
                  {addCqError}
                </span>
              )}
            </div>

            <div className="create-questions-drawer__footer">
              <button
                type="button"
                className="create-questions-btn create-questions-btn--secondary"
                onClick={closeAddCqDrawer}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`create-questions-btn create-questions-btn--primary${
                  addCqBqIndex === null
                    ? " create-questions-btn--deactivated"
                    : ""
                }`}
                disabled={
                  addCqBqIndex === null ||
                  !addCqQuestionText.trim() ||
                  isSavingAddCq
                }
                onClick={saveAddCqDrawer}
              >
                {isSavingAddCq ? "Adding\u2026" : "Add Question"}
              </button>
            </div>
          </aside>
        </div>
      )}

      {rowPendingDelete && (
        <div className="create-questions-confirm-root">
          <div
            className="create-questions-confirm-overlay"
            onClick={cancelDeleteRow}
          />
          <div className="create-questions-confirm-modal">
            <div className="create-questions-confirm-modal__content">
              <div className="create-questions-confirm-modal__heading">
                <p className="create-questions-confirm-modal__title">
                  Delete Competency Question?
                </p>
                <p className="create-questions-confirm-modal__subtitle">
                  You are about to delete this Competency Question.
                </p>
              </div>
              <div className="create-questions-confirm-modal__record">
                <p className="create-questions-confirm-modal__record-id">
                  CQ ID · {rowPendingDelete.cqId}
                </p>
                <p className="create-questions-confirm-modal__record-text">
                  “{rowPendingDelete.question}”
                </p>
              </div>
            </div>
            <div className="create-questions-confirm-modal__actions">
              <button
                type="button"
                className="create-questions-confirm-modal__cancel"
                onClick={cancelDeleteRow}
              >
                Cancel
              </button>
              <button
                type="button"
                className="create-questions-confirm-modal__delete"
                onClick={confirmDeleteRow}
              >
                Delete Competency Question
              </button>
            </div>
          </div>
        </div>
      )}

      {bqPendingDelete && (
        <div className="create-questions-confirm-root">
          <div
            className="create-questions-confirm-overlay"
            onClick={cancelDeleteBq}
          />
          <div className="create-questions-confirm-modal">
            <div className="create-questions-confirm-modal__content">
              <div className="create-questions-confirm-modal__heading">
                <p className="create-questions-confirm-modal__title">
                  Delete Business Question?
                </p>
                <p className="create-questions-confirm-modal__subtitle">
                  You are about to delete this Business Question.
                </p>
              </div>
              <div className="create-questions-confirm-modal__record">
                <p className="create-questions-confirm-modal__record-id">
                  BQ ID · {bqPendingDelete.entry.bqCode ?? "Not recorded yet"}
                </p>
                <p className="create-questions-confirm-modal__record-text">
                  “{bqPendingDelete.entry.question}”
                </p>
              </div>
            </div>
            <div className="create-questions-confirm-modal__actions">
              <button
                type="button"
                className="create-questions-confirm-modal__cancel"
                onClick={cancelDeleteBq}
              >
                Cancel
              </button>
              <button
                type="button"
                className="create-questions-confirm-modal__delete"
                onClick={confirmDeleteBq}
              >
                Delete Business Question
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateOntologyQuestions;
